import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

const root = document.querySelector('[data-hero-3d]');
const hero = document.querySelector('[data-hero-carousel]');
const configEl = document.getElementById('stack-hero-3d-config');
const settleEl = document.getElementById('stack-hero-3d-settle');
const scrollStory = document.querySelector('[data-scroll-story]');
if (!root || !hero || !configEl) throw new Error('stack-hero-3d root or config missing');

function setupStickyCanvasPin() {
  if (!scrollStory) return;
  const heroWrap = hero.closest('.shopify-section');
  const storyWrap = scrollStory.closest('.shopify-section');
  if (!heroWrap || !storyWrap || heroWrap === storyWrap) return;
  if (!(heroWrap.compareDocumentPosition(storyWrap) & Node.DOCUMENT_POSITION_FOLLOWING)) return;

  const parent = heroWrap.parentNode;
  if (!parent || parent.querySelector('.hero-v2-can-pin')) return;

  // Scope sticky to this wrapper only. No inline position styles — layout is CSS-only.
  const pin = document.createElement('div');
  pin.className = 'hero-v2-can-pin';
  const content = document.createElement('div');
  content.className = 'hero-v2-can-pin__content';

  parent.insertBefore(pin, heroWrap);
  pin.appendChild(root);
  pin.appendChild(content);
  content.appendChild(heroWrap);
  content.appendChild(storyWrap);

  hero.classList.add('full-width');
  scrollStory.classList.add('full-width');
  root.classList.add('is-pinned');

  const overlay = scrollStory.querySelector('[data-scroll-story-overlay]');
  if (overlay && !overlay.dataset.pinned) {
    overlay.classList.add('scroll-story__overlay--pinned');
    overlay.dataset.pinned = 'true';
    scrollStory.classList.add('has-pinned-overlay');
    pin.appendChild(overlay);
  }
}

setupStickyCanvasPin();

const config = JSON.parse(configEl.textContent);
const heroFrameDefaults = { canOffsetX: 0, canOffsetY: 0, zoom: 2.68 };
const heroFrame = { ...heroFrameDefaults };

function applyHeroFrameFromConfig(parsed) {
  heroFrame.canOffsetX = parsed.canOffsetX ?? 0;
  heroFrame.canOffsetY = parsed.canOffsetY ?? 0;
  heroFrame.zoom = parsed.zoom ?? heroFrameDefaults.zoom;
}

applyHeroFrameFromConfig(config);
const settleDefaults = {
  canOffsetX: 0,
  canOffsetY: -1,
  zoom: 4,
  scale: 4.8,
  tiltX: 0,
  tiltZ: 0,
  rotY: 0,
  shadowFloor: 0,
  handoffStartVh: 0.25,
  handoffEndVh: 0.15,
};
const SETTLE_BASE = { posX: 0.02, posY: -0.5 };
const settle = settleEl ? { ...settleDefaults, ...JSON.parse(settleEl.textContent) } : { ...settleDefaults };
settle.rotYRad = (settle.rotY * Math.PI) / 180;

function normalizeAssetUrl(url) {
  if (!url) return url;
  return new URL(url, window.location.href).href;
}

config.glb = normalizeAssetUrl(config.glb);
config.labels = config.labels.map(normalizeAssetUrl);

const CHERRY_INDEX = config.cherryLimeadeIndex ?? 1;
const U_OFFSET = -0.07;
const AUTO_SPIN = 0.12;
const FLIP_SPIN = Math.PI * 4;
const FLIP_SPIN_DUR = 420;
const DRAG_SENS = 0.011;
const DRAG_TILT_SENS = 0.003;
const MAX_DRAG_TILT_X = 0.26;
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (v) => Math.max(0, Math.min(1, v));
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);
const lerpAngle = (from, to, t) => {
  let delta = ((to - from) % (Math.PI * 2) + Math.PI * 3) % (Math.PI * 2) - Math.PI;
  return from + delta * t;
};

let handoffProgress = 0;
let handoffFlavorTriggered = false;
let handoffSnapshot = null;
let scrollMode = 'hero';

function isHandoffActive() {
  return handoffProgress > 0;
}

function handoffBlend(t) {
  return t * t * (3 - 2 * t);
}

function getSize() {
  const rect = root.getBoundingClientRect();
  return { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
}

function captureHandoffSnapshot() {
  if (!canRig || !canWrap) return;
  const layout = getHeroLayout();
  handoffSnapshot = {
    rigX: canRig.position.x,
    rigY: canRig.position.y,
    scale: canWrap.scale.x,
    cameraZ: camera.position.z,
    cameraX: camera.position.x,
    cameraY: camera.position.y,
    lookX: heroLookX || layout.lookX,
    lookY: heroLookY || layout.lookY,
    spin: spinY,
    tiltX: canRig.rotation.x,
    tiltZ: canRig.rotation.z,
    spinTarget: nearestForwardY(spinY),
  };
}

function updateHandoffProgress() {
  if (!scrollStory) {
    handoffProgress = 0;
    return;
  }

  const heroRect = hero.getBoundingClientRect();
  const storyRect = scrollStory.getBoundingClientRect();
  const heroBottomDoc = window.scrollY + heroRect.bottom;
  const storyTopDoc = window.scrollY + storyRect.top;
  const storyBottomDoc = storyTopDoc + scrollStory.offsetHeight;

  const start = heroBottomDoc - window.innerHeight * settle.handoffStartVh;
  const end = storyTopDoc + window.innerHeight * settle.handoffEndVh;
  const range = Math.max(window.innerHeight * 0.9, end - start);

  let progress = clamp01((window.scrollY - start) / range);
  if (window.scrollY + window.innerHeight < storyTopDoc - window.innerHeight * 0.5) {
    progress = 0;
  }
  if (window.scrollY > storyBottomDoc - window.innerHeight * 0.35) {
    progress = Math.min(progress, 1);
  }

  handoffProgress = progress;
  scrollMode = progress <= 0 ? 'hero' : progress >= 1 ? 'settled' : 'handoff';

  const isSettled = progress >= 0.985;
  if (isSettled !== updateHandoffProgress.wasSettled) {
    updateHandoffProgress.wasSettled = isSettled;
    scrollStory?.dispatchEvent(new CustomEvent('stack-can-settle', {
      detail: { settled: isSettled, progress },
    }));
  }

  if (progress <= 0) {
    handoffSnapshot = null;
    handoffFlavorTriggered = false;
  }

  root.classList.toggle('is-scroll-handoff', progress > 0);
  hero.classList.toggle('is-can-handoff', progress > 0);

  const handoffActive = progress > 0.05;
  if (handoffActive !== updateHandoffProgress.lastActive) {
    updateHandoffProgress.lastActive = handoffActive;
    hero.dispatchEvent(new CustomEvent('stack-hero-handoff', {
      detail: { active: handoffActive, flavorIndex: CHERRY_INDEX },
    }));
  }

  if (progress > 0 && !handoffFlavorTriggered && bodyMat) {
    handoffFlavorTriggered = true;
    selectFlavor(CHERRY_INDEX, { noSpin: true });
    flipSpin = null;
  }
}
updateHandoffProgress.lastActive = false;
updateHandoffProgress.wasSettled = false;

function nearestForwardY(y) {
  const tau = Math.PI * 2;
  return Math.round(y / tau) * tau + settle.rotYRad;
}

const pointer = { x: 0, y: 0 };
const drag = { active: false, pointerId: null, startX: 0, startY: 0, startSpinY: 0, startTiltX: 0, startTiltZ: 0 };
let spinY = 0;
let flipSpin = null;

function setPointerFromEvent(clientX, clientY) {
  const rect = root.getBoundingClientRect();
  pointer.x = ((clientX - rect.left) / rect.width) * 2 - 1;
  pointer.y = -((clientY - rect.top) / rect.height) * 2 + 1;
}

function bindInteraction(el) {
  el.addEventListener('pointerdown', (e) => {
    if (e.button !== 0 || e.pointerType === 'touch' || isHandoffActive()) return;
    drag.active = true;
    drag.pointerId = e.pointerId;
    drag.startX = e.clientX;
    drag.startY = e.clientY;
    drag.startSpinY = spinY;
    flipSpin = null;
    drag.startTiltX = tilt.x;
    drag.startTiltZ = tilt.z;
    root.classList.add('is-dragging');
    el.setPointerCapture(e.pointerId);
  });

  el.addEventListener('pointermove', (e) => {
    if (drag.active && e.pointerId === drag.pointerId) {
      const dx = e.clientX - drag.startX;
      const dy = e.clientY - drag.startY;
      spinY = drag.startSpinY + dx * DRAG_SENS;
      if (canWrap) canWrap.rotation.y = spinY;
      // Limit pitch so drag can't tip the can too far up/down.
      tilt.x = baseTiltX + clamp(drag.startTiltX + dy * DRAG_TILT_SENS - baseTiltX, MAX_DRAG_TILT_X);
      tilt.z = drag.startTiltZ - dx * 0.002;
      return;
    }
    if (!isHandoffActive()) setPointerFromEvent(e.clientX, e.clientY);
  });

  const endDrag = (e) => {
    if (!drag.active || e.pointerId !== drag.pointerId) return;
    drag.active = false;
    drag.pointerId = null;
    root.classList.remove('is-dragging');
    if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
  };

  el.addEventListener('pointerup', endDrag);
  el.addEventListener('pointercancel', endDrag);
  el.addEventListener('lostpointercapture', endDrag);
  el.addEventListener('mouseleave', () => {
    if (!drag.active) {
      pointer.x = 0;
      pointer.y = 0;
    }
  });
}

const { width: startW, height: startH } = getSize();
const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
renderer.setSize(startW, startH);
renderer.outputColorSpace = THREE.SRGBColorSpace;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 1.06;
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
root.appendChild(renderer.domElement);
bindInteraction(renderer.domElement);

const scene = new THREE.Scene();
const camera = new THREE.PerspectiveCamera(32, startW / startH, 0.01, 100);
camera.position.set(0, 0.04, 2.78);

const pmrem = new THREE.PMREMGenerator(renderer);
scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;

scene.add(new THREE.HemisphereLight(0xffffff, 0xedece2, 0.35));
const key = new THREE.DirectionalLight(0xfff4e2, 2.5);
key.position.set(2.2, 3.0, 2.4);
key.castShadow = true;
key.shadow.mapSize.set(1024, 1024);
key.shadow.camera.near = 0.5;
key.shadow.camera.far = 12;
key.shadow.camera.left = -1.2;
key.shadow.camera.right = 1.2;
key.shadow.camera.top = 1.2;
key.shadow.camera.bottom = -1.2;
scene.add(key);
const fill = new THREE.DirectionalLight(0xcfe0ff, 0.65);
fill.position.set(-2.8, 1.2, 1.4);
scene.add(fill);
const rim = new THREE.DirectionalLight(0xffffff, 1.6);
rim.position.set(-0.8, 2.0, -2.8);
scene.add(rim);

function shadowTex() {
  const s = 256;
  const c = document.createElement('canvas');
  c.width = c.height = s;
  const g = c.getContext('2d');
  const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
  grad.addColorStop(0, 'rgba(0,0,0,0.35)');
  grad.addColorStop(0.55, 'rgba(0,0,0,0.12)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, s, s);
  return new THREE.CanvasTexture(c);
}

const blob = new THREE.Mesh(
  new THREE.PlaneGeometry(1, 1),
  new THREE.MeshBasicMaterial({ map: shadowTex(), transparent: true, depthWrite: false })
);
blob.rotation.x = -Math.PI / 2;
scene.add(blob);

const _footBox = new THREE.Box3();
const HERO_SHADOW_DROP = 0.28;

function getSettledFootY() {
  if (!canWrap) return 0;
  canWrap.updateWorldMatrix(true, true);
  _footBox.setFromObject(canWrap);
  return _footBox.min.y + settle.shadowFloor;
}

function getHeroShadowY(rigY, bob = 0) {
  return -HERO_SHADOW_DROP + rigY + bob * 0.15;
}

function getShadowY(rigY, bob = 0) {
  const heroY = getHeroShadowY(rigY, bob);
  if (handoffProgress <= 0) return heroY;
  const settledY = getSettledFootY();
  if (handoffProgress >= 1) return settledY;
  return lerp(heroY, settledY, handoffBlend(handoffProgress));
}

let BODY_YMIN = 0.012;
let BODY_YMAX = 0.150;

function prepTex(t) {
  t.colorSpace = THREE.SRGBColorSpace;
  t.wrapS = THREE.RepeatWrapping;
  t.wrapT = THREE.ClampToEdgeWrapping;
  t.anisotropy = renderer.capabilities.getMaxAnisotropy();
  t.needsUpdate = true;
  return t;
}

const metalMat = new THREE.MeshStandardMaterial({ color: 0xdcdfe4, metalness: 0.97, roughness: 0.27, envMapIntensity: 1.2 });

let bodyMat = null;
let bodyShader = null;
function makeBodyMaterial(texA) {
  const m = new THREE.MeshStandardMaterial({ color: 0xffffff, metalness: 0.30, roughness: 0.36, envMapIntensity: 0.95 });
  m.map = texA;
  m.onBeforeCompile = (sh) => {
    sh.uniforms.uMapB = { value: texA };
    sh.uniforms.uMix = { value: 0 };
    sh.uniforms.uYmin = { value: BODY_YMIN };
    sh.uniforms.uYmax = { value: BODY_YMAX };
    sh.uniforms.uOff = { value: U_OFFSET };
    sh.vertexShader = 'varying vec3 vLP;\n' + sh.vertexShader.replace('#include <begin_vertex>', '#include <begin_vertex>\n  vLP=position;');
    sh.fragmentShader = 'varying vec3 vLP;\nuniform sampler2D uMapB;\nuniform float uMix;\nuniform float uYmin;\nuniform float uYmax;\nuniform float uOff;\n'
      + sh.fragmentShader.replace('#include <map_fragment>', `
        #ifdef USE_MAP
          float cylTau=6.283185307179586;
          float ang=atan(vLP.z,vLP.x);
          vec2 cuv=vec2(0.5 - ang/cylTau + uOff, (vLP.y-uYmin)/(uYmax-uYmin));
          vec4 ca=texture2D(map,cuv);
          vec4 cb=texture2D(uMapB,cuv);
          diffuseColor *= mix(ca,cb,clamp(uMix,0.0,1.0));
        #endif
      `);
    bodyShader = sh;
  };
  m.customProgramCacheKey = () => 'stack-hero-cyl-mix';
  return m;
}

const texLoader = new THREE.TextureLoader();
texLoader.setCrossOrigin('anonymous');
const gltfLoader = new GLTFLoader();

let canWrap = null;
let canRig = null;
let currentFlavor = 0;
let textures = [];
let swap = null;
let canAnchorX = 0.14;
let canAnchorY = 0;
let canXOffset = 0;
let canYOffset = 0;
let canScale = 5.7;
let heroCameraZ = 2.68;
let heroLookX = 0.14;
let heroLookY = 0;
const baseTiltZ = -0.22;
const baseTiltX = 0.02;
const BASE_CAMERA_Y = 0.04;
const MAX_TILT_OFFSET = 0.07;
const clamp = (v, max) => Math.max(-max, Math.min(max, v));
const tilt = { x: baseTiltX, z: baseTiltZ };
const targetTilt = { x: baseTiltX, z: baseTiltZ };

function cameraYOffsetFromY(cameraY) {
  return cameraY - BASE_CAMERA_Y;
}

function cameraLookAtY(anchorY, cameraY) {
  return anchorY + cameraYOffsetFromY(cameraY);
}

function getHeroBase(wide) {
  const lookX = wide ? 0.14 : 0.94;
  const lookY = wide ? 0 : -0.02;
  return {
    lookX,
    lookY,
    rigOffsetX: wide ? 0.09 : 0,
    rigOffsetY: wide ? -0.40 : -0.24,
    scale: wide ? 5.7 : 4.55,
    cameraZ: wide ? 2.68 : 2.88,
  };
}

function getHeroLayout() {
  const heroRect = hero.getBoundingClientRect();
  const aspect = heroRect.width / Math.max(1, heroRect.height);
  const wide = aspect > 1.1;
  const base = getHeroBase(wide);
  const offsetX = heroFrame.canOffsetX ?? 0;
  const offsetY = heroFrame.canOffsetY ?? 0;
  const cameraZ = heroFrame.zoom ?? base.cameraZ;
  return {
    lookX: base.lookX,
    lookY: base.lookY,
    rigX: base.lookX + base.rigOffsetX + offsetX,
    rigY: base.lookY + base.rigOffsetY + offsetY,
    scale: base.scale,
    cameraZ,
    cameraY: BASE_CAMERA_Y,
    tiltX: baseTiltX,
    tiltZ: baseTiltZ,
  };
}

function getSettleLayout() {
  const offsetX = settle.canOffsetX ?? 0;
  const offsetY = settle.canOffsetY ?? 0;
  return {
    lookX: SETTLE_BASE.posX,
    lookY: SETTLE_BASE.posY,
    rigX: SETTLE_BASE.posX + offsetX,
    rigY: SETTLE_BASE.posY + offsetY,
    scale: settle.scale,
    cameraX: 0,
    cameraY: BASE_CAMERA_Y,
    cameraZ: settle.zoom,
    tiltX: settle.tiltX,
    tiltZ: settle.tiltZ,
    rotY: settle.rotYRad,
  };
}

function refreshSettleConfig() {
  const el = document.getElementById('stack-hero-3d-settle');
  if (!el) return;
  try {
    Object.assign(settle, settleDefaults, JSON.parse(el.textContent));
    settle.rotYRad = (settle.rotY * Math.PI) / 180;
    handoffSnapshot = null;
  } catch (_) {}
  observeSettleConfig();
  if (!isHandoffActive()) layoutCan();
}

function refreshHeroConfig() {
  const el = document.getElementById('stack-hero-3d-config');
  if (!el) return;
  try {
    applyHeroFrameFromConfig(JSON.parse(el.textContent));
    handoffSnapshot = null;
  } catch (_) {}
  observeHeroConfig();
  if (!isHandoffActive()) layoutCan();
}

let heroConfigObserver = null;
let observedHeroConfigEl = null;

function observeHeroConfig() {
  const el = document.getElementById('stack-hero-3d-config');
  if (!el || el === observedHeroConfigEl) return;
  observedHeroConfigEl = el;
  if (!heroConfigObserver) {
    heroConfigObserver = new MutationObserver(refreshHeroConfig);
  } else {
    heroConfigObserver.disconnect();
  }
  heroConfigObserver.observe(el, { characterData: true, childList: true, subtree: true });
}

let settleConfigObserver = null;
let observedSettleEl = null;

function observeSettleConfig() {
  const el = document.getElementById('stack-hero-3d-settle');
  if (!el || el === observedSettleEl) return;
  observedSettleEl = el;
  if (!settleConfigObserver) {
    settleConfigObserver = new MutationObserver(refreshSettleConfig);
  } else {
    settleConfigObserver.disconnect();
  }
  settleConfigObserver.observe(el, { characterData: true, childList: true, subtree: true });
}

function layoutCan() {
  if (!canWrap || isHandoffActive()) return;
  const layout = getHeroLayout();
  canAnchorX = layout.lookX;
  canAnchorY = layout.lookY;
  canXOffset = layout.rigX - layout.lookX;
  canYOffset = layout.rigY - layout.lookY;
  canScale = layout.scale;
  heroCameraZ = layout.cameraZ;
  heroLookX = layout.lookX;
  heroLookY = layout.lookY;
  canWrap.position.x = 0;
  canWrap.scale.setScalar(canScale);
  camera.position.set(0, layout.cameraY, heroCameraZ);
  camera.lookAt(heroLookX, cameraLookAtY(heroLookY, layout.cameraY), 0);
  if (canRig) {
    canRig.position.x = layout.rigX;
    canRig.position.y = layout.rigY;
  }
  updateShadow(layout.rigX, canScale, layout.rigY);
}

function updateShadow(x, scale, rigY = 0, bob = 0) {
  if (!canWrap || !blob) return;
  const footR = 0.22 * scale;
  blob.scale.set(footR, footR * 0.55, 1);
  blob.position.set(x, getShadowY(rigY, bob), 0);
}

function selectFlavor(id, opts = {}) {
  const instant = opts === true || opts.instant;
  const noSpin = opts === true ? false : opts.noSpin;
  if (!textures.length || !bodyMat) return;
  id = (id + textures.length) % textures.length;
  if (instant || id === currentFlavor) {
    if (bodyMat) bodyMat.map = textures[id];
    currentFlavor = id;
    return;
  }
  if (bodyShader) {
    bodyShader.uniforms.uMapB.value = textures[id];
    swap = { from: performance.now(), dur: 650, target: id };
  } else {
    bodyMat.map = textures[id];
    currentFlavor = id;
  }

  if (!noSpin) {
    // Land on a forward-facing angle: spin at least FLIP_SPIN, then round up
    // to the next full turn so the label ends up facing the camera.
    const tau = Math.PI * 2;
    const endY = Math.ceil((spinY + FLIP_SPIN) / tau) * tau;
    flipSpin = { from: performance.now(), startY: spinY, endY, dur: FLIP_SPIN_DUR };
  }
}

hero.addEventListener('stack-hero-flavor', (e) => {
  if (isHandoffActive()) return;
  selectFlavor(e.detail?.index ?? 0);
});

async function build() {
  textures = await Promise.all(config.labels.map(async (url) => {
    try {
      return prepTex(await texLoader.loadAsync(url));
    } catch (error) {
      console.error('stack-hero-3d: label failed to load', url, error);
      throw error;
    }
  }));
  let gltf;
  try {
    gltf = await gltfLoader.loadAsync(config.glb);
  } catch (error) {
    console.error('stack-hero-3d: model failed to load', config.glb, error);
    throw error;
  }
  const model = gltf.scene.clone();

  let body = null;
  let tallestH = -1;
  model.traverse((n) => {
    if (n.isMesh) {
      n.geometry.computeBoundingBox();
      const bb = n.geometry.boundingBox;
      const h = bb.max.y - bb.min.y;
      if (h > tallestH) {
        tallestH = h;
        body = n;
      }
    }
  });

  const bb = body.geometry.boundingBox;
  const H = bb.max.y - bb.min.y;
  BODY_YMIN = bb.min.y + H * 0.085;
  BODY_YMAX = bb.max.y - H * 0.040;

  bodyMat = makeBodyMaterial(textures[0]);
  model.traverse((n) => {
    if (n.isMesh) {
      n.castShadow = true;
      n.receiveShadow = true;
      n.material = n === body ? bodyMat : metalMat;
    }
  });

  model.position.y = -bb.min.y;

  canWrap = new THREE.Group();
  canWrap.add(model);
  canWrap.scale.setScalar(canScale);

  canRig = new THREE.Group();
  canRig.position.set(canAnchorX, canAnchorY, 0);
  canRig.add(canWrap);
  scene.add(canRig);

  layoutCan();
  selectFlavor(0, true);
  root.classList.add('is-ready');
  animate();
}

const clock = new THREE.Clock();

function applyHandoffLayout(t) {
  const settleLayout = getSettleLayout();
  const k = handoffBlend(t);
  const start = handoffSnapshot || {
    rigX: canRig.position.x,
    rigY: canRig.position.y,
    scale: canWrap.scale.x,
    cameraZ: camera.position.z,
    cameraX: camera.position.x,
    cameraY: camera.position.y,
    lookX: heroLookX,
    lookY: heroLookY,
    spin: spinY,
    tiltX: canRig.rotation.x,
    tiltZ: canRig.rotation.z,
    spinTarget: nearestForwardY(spinY),
  };

  const rigX = lerp(start.rigX, settleLayout.rigX, k);
  const rigY = lerp(start.rigY, settleLayout.rigY, k);
  const scale = lerp(start.scale, settleLayout.scale, k);
  const cameraZ = lerp(start.cameraZ, settleLayout.cameraZ, k);
  const cameraX = lerp(start.cameraX ?? 0, settleLayout.cameraX, k);
  const cameraY = lerp(start.cameraY, settleLayout.cameraY, k);
  const lookX = lerp(start.lookX, settleLayout.lookX, k);
  const lookY = lerp(start.lookY, settleLayout.lookY, k);
  const tiltX = lerp(start.tiltX, settleLayout.tiltX, k);
  const tiltZ = lerp(start.tiltZ, settleLayout.tiltZ, k);
  const rotY = lerpAngle(start.spin, start.spinTarget, k);

  canRig.position.x = rigX;
  canRig.position.y = rigY;
  canRig.rotation.x = tiltX;
  canRig.rotation.z = tiltZ;
  canWrap.rotation.y = rotY;
  canWrap.scale.setScalar(scale);
  camera.position.set(cameraX, cameraY, cameraZ);
  camera.lookAt(lookX, lookY, 0);

  spinY = rotY;
  tilt.x = tiltX;
  tilt.z = tiltZ;

  updateShadow(rigX, scale, rigY);
}

function animate() {
  requestAnimationFrame(animate);
  const delta = clock.getDelta();
  const elapsed = clock.getElapsedTime();

  updateHandoffProgress();

  const { width, height } = getSize();
  if (renderer.domElement.width !== Math.floor(width * renderer.getPixelRatio()) ||
      renderer.domElement.height !== Math.floor(height * renderer.getPixelRatio())) {
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setSize(width, height);
  }

  if (canRig && canWrap) {
    const t = handoffProgress;

    if (t > 0) {
      if (!handoffSnapshot) captureHandoffSnapshot();
      applyHandoffLayout(t);
    } else {
      const bob = Math.sin(elapsed * 0.9) * 0.012 + Math.sin(elapsed * 0.5) * 0.006;
      const layout = getHeroLayout();
      canRig.position.y = layout.rigY + bob;
      canRig.position.x = layout.rigX;

      if (flipSpin) {
        const ft = Math.min(1, (performance.now() - flipSpin.from) / flipSpin.dur);
        const endY = flipSpin.endY ?? flipSpin.startY + FLIP_SPIN;
        spinY = lerp(flipSpin.startY, endY, easeOutQuart(ft));
        canWrap.rotation.y = spinY;
        if (ft >= 1) flipSpin = null;
      } else if (!drag.active) {
        spinY += AUTO_SPIN * delta;
        canWrap.rotation.y = spinY;

        targetTilt.x = baseTiltX + clamp(pointer.y * 0.1, MAX_TILT_OFFSET);
        targetTilt.z = baseTiltZ + clamp(-pointer.x * 0.1, MAX_TILT_OFFSET);
        tilt.x = lerp(tilt.x, targetTilt.x, 0.07);
        tilt.z = lerp(tilt.z, targetTilt.z, 0.07);
      }

      canRig.rotation.x = tilt.x;
      canRig.rotation.z = tilt.z;

      camera.position.set(0, layout.cameraY, layout.cameraZ);
      camera.lookAt(layout.lookX, cameraLookAtY(layout.lookY, layout.cameraY), 0);
      heroLookX = layout.lookX;
      heroLookY = layout.lookY;
      heroCameraZ = layout.cameraZ;

      updateShadow(canRig.position.x, canWrap.scale.x, layout.rigY, bob);
    }
  }

  if (swap && bodyShader) {
    const st = Math.min(1, (performance.now() - swap.from) / swap.dur);
    bodyShader.uniforms.uMix.value = easeInOut(st);
    if (st >= 1) {
      bodyMat.map = textures[swap.target];
      bodyShader.uniforms.uMix.value = 0;
      bodyShader.uniforms.uMapB.value = textures[swap.target];
      currentFlavor = swap.target;
      swap = null;
    }
  }

  renderer.render(scene, camera);
}

function onResize() {
  if (!isHandoffActive()) layoutCan();
}

if ('ResizeObserver' in window) {
  new ResizeObserver(onResize).observe(root);
} else {
  addEventListener('resize', onResize);
}
addEventListener('scroll', updateHandoffProgress, { passive: true });
addEventListener('resize', updateHandoffProgress, { passive: true });
document.addEventListener('shopify:section:load', () => {
  refreshHeroConfig();
  refreshSettleConfig();
});
document.addEventListener('shopify:section:unload', () => {
  refreshHeroConfig();
  refreshSettleConfig();
});
observeHeroConfig();
observeSettleConfig();

build().catch((err) => {
  console.error('stack-hero-3d:', err);
  root.classList.add('is-error');
});
